import { useMemo, useState } from "react";

import { useTranslate } from "@similarweb/i18n-provider";
import { IconButton, Tooltip } from "@similarweb/ui-components";
import { Flex, Stack } from "@similarweb/ui-components/foundations";

import { FiltersChip } from "app/pages/sales-intelligence/common-components/filters-panel";
import {
    ScoringConfigType,
    type IScoringRuleDto,
} from "app/pages/sales-intelligence/domain/leads/types/scoring-config";
import { useFiltersManager } from "app/pages/sales-intelligence/modules/advanced-search-filtering/hooks/useFiltersManager";
import type { IFilterInstance } from "app/pages/sales-intelligence/modules/advanced-search-filtering/types/filterInstance";
import { scoringRuleToPanelValue } from "app/pages/sales-intelligence/modules/lead-scoring-agent/utils/scoringRuleAdapters";
import { CrmAppliedIndicator } from "app/pages/sales-intelligence/modules/leads-search-filters/components/selected-filters-sidebar/CrmAppliedIndicator";
import { SelectedLeadsFilterCriteriaSections } from "app/pages/sales-intelligence/modules/leads-search-filters/components/selected-filters-sidebar/SelectedLeadsFilterCriteriaSections";
import type { LeadsSearchAllFiltersTypes } from "app/pages/sales-intelligence/modules/leads-search-filters/types/leadsSearchFilters.dto";
import type { SignalByIdMap } from "app/pages/sales-intelligence/sub-modules/signals/types/extended-models";

import { ScoringRuleTeamsPreview } from "./ScoringRuleTeamsPreview";
import { ScoringRuleUsedBadge } from "./ScoringRuleUsedBadge";
import { ScoringScopeBadge, type ScoringScopeBadgeKeys } from "./ScoringScopeBadge";
import {
    StyledExpandableFilterChip,
    StyledRuleCard,
    StyledRuleCardActionItemText,
    StyledRuleCardActions,
    StyledRuleCardContextRow,
    StyledRuleCardContextText,
    StyledRuleCardHeader,
} from "./styles";

type Props = {
    rule: IScoringRuleDto;
    filtersList: readonly IFilterInstance<LeadsSearchAllFiltersTypes>[];
    signalById: SignalByIdMap;
    onEdit?: () => void;
    onDelete?: () => void;
    scope?: ScoringConfigType;
    scopeBadgeKeys?: ScoringScopeBadgeKeys;
    usedByPlaybookNames?: readonly string[];
    /** Selecting a rule is what marks it as used on boards that assign rules rather than pick one. */
    showUsedBadgeForSelected?: boolean;
    selection?: {
        isSelected: boolean;
        onSelect: () => void;
        /** Set when the board cannot toggle this rule; the text explains why to the user. */
        disabledReason?: string;
    };
};

const noop = () => undefined;

export const ScoringRuleCard = ({
    rule,
    filtersList,
    signalById,
    onEdit,
    onDelete,
    scope,
    scopeBadgeKeys,
    usedByPlaybookNames = [],
    showUsedBadgeForSelected = false,
    selection,
}: Props) => {
    const { translate } = useTranslate();
    const [isCollapsed, setIsCollapsed] = useState(true);
    const isUsed = usedByPlaybookNames.length > 0 || (showUsedBadgeForSelected && !!selection?.isSelected);

    // Reconstruct the flat panel value the filter panel + lead-gen agent operate on:
    // signals expand into per-type `signals<Type>` arrays, leads filters are pass-through.
    const panelValue = useMemo(() => scoringRuleToPanelValue(rule, signalById), [rule, signalById]);

    // Read-only filters manager: same shape the prospecting agent uses, but no-op write callbacks
    // since the card never mutates a rule directly (edits go through the wizard modal).
    const filtersManager = useFiltersManager<LeadsSearchAllFiltersTypes>({
        filtersValue: panelValue,
        filtersConfig: filtersList,
        onFiltersUpdate: noop,
    });

    const emptyCriteriaMessage = translate("si.pages.score_settings.card.empty_criteria");

    const filterCount = filtersManager.getDirtyFilters().length;

    const filterCountLabel = translate(
        filterCount === 1
            ? "si.pages.score_settings.card.filters_count.single"
            : "si.pages.score_settings.card.filters_count.plural",
        { count: filterCount },
    );

    const businessContextLabel = rule.businessContext?.translation;
    const actionItem = rule.businessContext?.actionItem;

    const selectionDisabledReason = selection?.disabledReason;
    const isSelectionDisabled = selectionDisabledReason !== undefined;

    const handleCollapseToggle = () => setIsCollapsed((prev) => !prev);
    const handleExpand = () => setIsCollapsed(false);
    const handleCardClick = selection && !isSelectionDisabled ? selection.onSelect : undefined;
    const showManageActions = onEdit !== undefined && onDelete !== undefined && !isSelectionDisabled;
    const hasAssignedTeams = (rule.teamIds?.length ?? 0) > 0;
    const showTeamsPreview = scope === ScoringConfigType.Account && hasAssignedTeams;

    return (
        <Tooltip
            placement="top"
            enabled={isSelectionDisabled}
            content={selectionDisabledReason}
        >
            <StyledRuleCard
                data-automation="scoring-rule-card"
                aria-disabled={isSelectionDisabled}
                $isSelected={selection?.isSelected}
                $isSelectable={!!selection && !isSelectionDisabled}
                $isDisabled={isSelectionDisabled}
                onClick={handleCardClick}
            >
                <StyledRuleCardHeader>
                    <StyledRuleCardContextRow>
                        <StyledRuleCardContextText data-automation="scoring-rule-card.context">
                            {businessContextLabel || translate("si.pages.score_settings.card.untitled")}
                        </StyledRuleCardContextText>
                        <StyledRuleCardActions>
                            {showManageActions ? (
                                <>
                                    <Tooltip
                                        placement="top"
                                        content={translate("si.pages.score_settings.card.tooltip.edit")}
                                    >
                                        <IconButton
                                            type="flat"
                                            iconName="edit-icon"
                                            iconSize="xs"
                                            onClick={(event) => {
                                                event.stopPropagation();
                                                onEdit();
                                            }}
                                            dataAutomation="scoring-rule-card.edit"
                                        />
                                    </Tooltip>
                                    <Tooltip
                                        placement="top"
                                        content={translate("si.pages.score_settings.card.tooltip.delete")}
                                    >
                                        <IconButton
                                            type="flat"
                                            iconName="delete"
                                            iconSize="xs"
                                            onClick={(event) => {
                                                event.stopPropagation();
                                                onDelete();
                                            }}
                                            dataAutomation="scoring-rule-card.delete"
                                        />
                                    </Tooltip>
                                </>
                            ) : null}
                            <Tooltip
                                placement="top"
                                content={translate(
                                    isCollapsed
                                        ? "si.pages.score_settings.card.tooltip.expand"
                                        : "si.pages.score_settings.card.tooltip.collapse",
                                )}
                            >
                                <IconButton
                                    type="flat"
                                    iconName={isCollapsed ? "chev-down" : "chev-up"}
                                    iconSize="xs"
                                    onClick={(event) => {
                                        event.stopPropagation();
                                        handleCollapseToggle();
                                    }}
                                    dataAutomation="scoring-rule-card.collapse"
                                />
                            </Tooltip>
                        </StyledRuleCardActions>
                    </StyledRuleCardContextRow>
                    {actionItem ? (
                        <StyledRuleCardActionItemText data-automation="scoring-rule-card.action-item">
                            {actionItem}
                        </StyledRuleCardActionItemText>
                    ) : null}
                </StyledRuleCardHeader>

                {isCollapsed ? (
                    <StyledExpandableFilterChip
                        onClick={(event) => {
                            event.stopPropagation();
                            handleExpand();
                        }}
                        data-automation="scoring-rule-card.filter-count"
                    >
                        <FiltersChip
                            text={filterCountLabel}
                            hideActionButton
                        />
                    </StyledExpandableFilterChip>
                ) : null}

                {isCollapsed ? null : (
                    <Stack gap="8px">
                        <CrmAppliedIndicator panelValue={panelValue} />
                        <SelectedLeadsFilterCriteriaSections
                            filtersManager={filtersManager}
                            filtersList={filtersList}
                            emptyMessage={emptyCriteriaMessage}
                        />
                    </Stack>
                )}

                {scope || isUsed || showTeamsPreview ? (
                    <Flex
                        gap="8px"
                        width="fit-content"
                        alignItems="center"
                        onClick={(event) => event.stopPropagation()}
                    >
                        {scope ? (
                            <ScoringScopeBadge
                                scope={scope}
                                scopeBadgeKeys={scopeBadgeKeys}
                                showSharedTooltip={!hasAssignedTeams && !isSelectionDisabled}
                            />
                        ) : null}
                        {showTeamsPreview ? <ScoringRuleTeamsPreview teamIds={rule.teamIds} /> : null}
                        {isUsed ? <ScoringRuleUsedBadge playbookNames={usedByPlaybookNames} /> : null}
                    </Flex>
                ) : null}
            </StyledRuleCard>
        </Tooltip>
    );
};

// Reused by ScoringBoardContainer to drop the StyledRuleChips export from the previous flat-chip
// implementation; the per-filter `filterResultView` now owns chip layout.
export type ScoringRuleCardProps = Props;
